<template>
  <header class="header-wrapper flex z-50 items-center border-b dark:border-dark-400">
    <div id="header" class="flex h-full" style="width: 41%">
      <HeaderDropDownMenu
        v-if="!authStatePresenter.isLogin.value"
        class="cursor-pointer"
        :menuClickList="[
          () => modalStatePresenter.setIsShowSettingsModal(true),
          () => modalStatePresenter.setIsShowLoginModal(true)
        ]"
        :menuIconList="['settings', 'login']"
        :menuTextList="[$t('message.settings'), $t('message.login')]"
        :titleIcon="'no_accounts'"
        :titleText="$t('message.user')"
      />
      <HeaderDropDownMenu
        v-else-if="nested.avatarImage.value != undefined || nested.avatarImage.value == ''"
        class="cursor-pointer"
        :menuClickList="[
          () => modalStatePresenter.setIsShowDashboardModal(true),
          () => modalStatePresenter.setIsShowSettingsModal(true),
          () => authStatePresenter.logout()
        ]"
        :menuIconList="['dashboard', 'settings', 'logout']"
        :menuTextList="[$t('message.dashboard'), $t('message.settings'), $t('message.logout')]"
        :titleIcon="'account_circle'"
        :titleImage="nested.avatarImage.value"
        :titleText="$t('message.user')"
      />
      <HeaderDropDownMenu
        v-else
        class="cursor-pointer"
        :menuClickList="[
          () => modalStatePresenter.setIsShowDashboardModal(true),
          () => modalStatePresenter.setIsShowSettingsModal(true),
          () => authStatePresenter.logout()
        ]"
        :menuIconList="['dashboard', 'settings', 'logout']"
        :menuTextList="[$t('message.dashboard'), $t('message.settings'), $t('message.logout')]"
        :titleIcon="'account_circle'"
        :titleText="$t('message.user')"
      />
      <HeaderDropDownMenu
        id="problemSearch"
        class="cursor-pointer"
        :menuClickList="[
          () => modalStatePresenter.openProblemSearchModal('COURSE'),
          () => modalStatePresenter.openProblemSearchModal('CHALLENGE'),
          () => modalStatePresenter.openProblemSearchModal('BOOKMARK'),
          () => modalStatePresenter.setIsShowArenaModal(true)
        ]"
        :menuIconList="['map', 'inbox', 'bookmark_border', 'flag']"
        :menuTextList="[$t('message.course'), $t('message.challenge'), $t('message.bookmark'), $t('message.arena')]"
        :titleIcon="'assignment'"
        :titleText="$t('message.problems')"
      />
      <HeaderDropDownMenu
        class="cursor-pointer"
        :menuClickList="[clickAOJ1, clickAOJ2, () => modalStatePresenter.setIsShowFreeSpaceModal(true)]"
        :menuIconList="['home', 'account_balance', 'space_dashboard']"
        :menuTextList="['AOJ 1\.0', 'AOJ 2\.0', $t('message.freeSpace')]"
        :titleIcon="'public'"
        :titleText="$t('message.links')"
      />
      <HeaderDropDownMenu
        class="cursor-pointer"
        :menuClickList="[clickMail, () => modalStatePresenter.setIsShowUpdateModal(true), clickTour, clickHelp]"
        :menuIconList="['send', 'update', 'flight', 'info']"
        :menuTextList="[$t('message.contact'), $t('message.update'), $t('message.tour'), $t('message.tutorial')]"
        :titleIcon="'help'"
        :titleText="$t('message.help')"
      />
    </div>
    <div class="flex-grow h-full" />
    <ProblemInfoLabel />
    <div class="flex-grow h-full" />
    <div class="flex h-full" style="width: 40%">
      <div class="w-full flex justify-end">
        <HeaderDropDownMenu
          class="mr-4 cursor-pointer"
          :itemPY="'py-1'"
          :menuClickList="languageClickList"
          :menuTextList="programLanguageList"
          :titleText="programLanguage"
        />
        <IconForm
          class="shadow-md mr-2"
          :height="'40px'"
          :placeholder="$t('message.inputProblemId')"
          @enterForm="enterForm"
        />
      </div>
    </div>
  </header>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'

import HeaderDropDownMenu from './children/HeaderDropDownMenu.vue'
import ProblemInfoLabel from './children/ProblemInfoLabel.vue'

import { programLanguageList, ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import IconForm from '@/presentation/components/Input/IconForm.vue'
import { useArenaPresenter } from '@/presentation/composables/arena/useArenaPresenter'
import { useAuthStatePresenter } from '@/presentation/composables/auth/useAuthStatePresenter'
import { useConfigStatePresenter } from '@/presentation/composables/config/useConfigStatePresenter'
import { useModalStatePresenter } from '@/presentation/composables/modal/useModalStatePresenter'
import { useProblemStatePresenter } from '@/presentation/composables/problem/useProblemStatePresenter'
import { useIntroPresenter } from '@/presentation/composables/util/useIntroPresenter'

export default defineComponent({
  name: 'PrimaryHeader',
  components: {
    HeaderDropDownMenu,
    ProblemInfoLabel,
    IconForm
  },
  setup(props, ctx) {
    const modalStatePresenter = useModalStatePresenter()
    const { setProgramLanguage, programLanguage, displayLanguage } = useConfigStatePresenter()
    const { setProblemId } = useProblemStatePresenter()
    const arenaPresenter = useArenaPresenter()
    const authStatePresenter = useAuthStatePresenter()
    const { startTour } = useIntroPresenter()

    const isLogin = computed(() => authStatePresenter.isLogin.value)
    const avatarImage = computed(() => authStatePresenter.user.value.avatar)
    const searchedProblemId = ref('')
    const selectedProgramLanguageIndex = ref(0)

    const newLanguages = programLanguageList.map(lang => {
      return lang
    })

    watch(
      () => programLanguage.value,
      newValue => {
        selectedProgramLanguageIndex.value = programLanguageList.findIndex(lang => lang == newValue)
      },
      { immediate: true }
    )

    const enterForm = (id: string) => {
      const nid = escapeSpace(id)
      if (nid == '') return
      arenaPresenter.setArenaProblem('', '', '', -1)
      setProblemId(nid)
    }

    const escapeSpace = (text: string) => {
      if (typeof text !== 'string') {
        return text
      }
      let ntext = escapeTextSpace(text)
      return ntext
    }

    const escapeTextSpace = (str: string) => {
      return str
        .replace(/\\/g, '')
        .replace(/'/g, '')
        .replace(/"/g, '')
        .replace(/\//g, '')
        .replace(/</g, '')
        .replace(/>/g, '')
        .replace(/#/g, '')
        .replace(/\$/g, '')
        .replace(/%/g, '')
        .replace(/=/g, '')
        .replace(/~/g, '')
        .replace(/\^/g, '')
        .replace(/\|/g, '')
        .replace(/\?/g, '')
        .replace(/{/g, '')
        .replace(/}/g, '')
        .replace(/!/g, '')
        .replace(/\(/g, '')
        .replace(/\)/g, '')
        .replace(/(0x0D)/g, '')
        .replace(/(0x0A)/g, '')
        .replace(/&/g, '')
    }

    const isProgramLanguageType = (value: ProgramLanguageType): value is ProgramLanguageType => {
      return programLanguageList.includes(value as ProgramLanguageType)
    }

    const selectProgramLanguage = (value: ProgramLanguageType) => {
      if (isProgramLanguageType(value)) {
        setProgramLanguage(value)
      }
    }

    let languageClickList = []
    for (let i = 0; i < programLanguageList.length; i++) {
      languageClickList.push(() => selectProgramLanguage(programLanguageList[i]))
    }

    const clickHelp = () => {
      if (displayLanguage.value == 'Japanese') {
        window.open('https://onlinejudge.u-aizu.ac.jp/documents/ICE_ja.pdf', '_blank', 'noopener')
      } else {
        window.open('https://onlinejudge.u-aizu.ac.jp/documents/ICE_en.pdf', '_blank', 'noopener')
      }
    }

    const clickAOJ1 = () => {
      window.open('https://judge.u-aizu.ac.jp/onlinejudge/', '_blank', 'noopener')
    }

    const clickAOJ2 = () => {
      window.open('https://onlinejudge.u-aizu.ac.jp/home', '_blank', 'noopener')
    }

    const clickTour = () => {
      startTour()
    }

    const clickMail = () => {
      window.location.href = 'mailto:uoaojs@gmail.com'
    }

    const clickIcon = () => {
      window.location.reload()
    }

    return {
      authStatePresenter,
      modalStatePresenter,
      clickMail,
      clickTour,
      selectProgramLanguage,
      clickHelp,
      clickAOJ1,
      clickAOJ2,
      clickIcon,
      enterForm,
      programLanguageList,
      programLanguage,
      arenaPresenter,
      newLanguages,
      languageClickList,
      nested: {
        searchedProblemId,
        isLogin,
        avatarImage,
        selectedProgramLanguageIndex
      }
    }
  }
})
</script>

<style lang="postcss" scoped>
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  transition: background-color 5000000s ease-in-out 0s;
}

.header-wrapper {
  height: 70px;

  & > * {
    @apply mr-2;
  }

  & > :first-child {
    @apply mr-0;
  }
}

.primary {
  @apply bg-aoj border-2 border-aoj text-white dark:bg-dark-200 dark:border-dark-100 dark:text-dark-text;

  &:active {
    @apply bg-aoj-click dark:bg-dark-100 dark:border-dark-10;
  }

  &:focus {
    @apply outline-none;
  }
}
</style>
